/**
 * Prontidão da padronização. Não é eficácia e não gera score.
 * A máquina permite `validation → closed` e `monitoring → closed` sem estes itens.
 * No Problema Estruturado o CTA de Conclusão só sai de `monitoring`, com eficácia P4
 * ainda válida e os quatro registros abaixo. O contrato de auditoria exige resultado
 * (`pass` | `fail` | `partial`): não há registro de auditoria apenas planejada.
 * Treinamento não tem lista de pessoas; um registro com título cobre o item.
 */

export const STANDARDIZATION_KINDS = ['standard', 'training', 'communication', 'audit'] as const;

export type StandardizationKind = (typeof STANDARDIZATION_KINDS)[number];

export type AuditResult = 'pass' | 'fail' | 'partial';

export interface StandardizationEventRef {
  kind: string;
}

export interface StandardizationReadiness {
  standard: boolean;
  training: boolean;
  communication: boolean;
  audit: boolean;
  recorded: number;
  total: 4;
  ready: boolean;
}

const ITEM_BY_KIND: Record<StandardizationKind, keyof Pick<StandardizationReadiness, 'standard' | 'training' | 'communication' | 'audit'>> = {
  standard: 'standard',
  training: 'training',
  communication: 'communication',
  audit: 'audit',
};

export function standardizationReadiness(events: StandardizationEventRef[]): StandardizationReadiness {
  const present = {
    standard: false,
    training: false,
    communication: false,
    audit: false,
  };
  events.forEach((event) => {
    const item = ITEM_BY_KIND[event.kind as StandardizationKind];
    if (item) present[item] = true;
  });
  const recorded = Object.values(present).filter(Boolean).length;
  return { ...present, recorded, total: 4, ready: recorded === 4 };
}

/** Eficácia e transição continuam sendo decididas pelo chamador. */
export function standardizationAllowsConclusion(input: {
  efficacyValid: boolean;
  events: StandardizationEventRef[];
  transitionAllowed: boolean;
}): boolean {
  return input.efficacyValid && standardizationReadiness(input.events).ready && input.transitionAllowed;
}

export function standardizationEventPayload(input: {
  kind: StandardizationKind;
  title?: string | null;
  body?: string | null;
  audience?: string | null;
  result?: string | null;
}): {
  title: string | null;
  body: string | null;
  audience: string | null;
  result: AuditResult | null;
} {
  const title = input.title?.trim() || null;
  const body = input.body?.trim() || null;
  const audience = input.audience?.trim() || null;
  const result = input.result?.trim() || null;
  if (input.kind === 'standard' && !title) throw new Error('Informe o padrão atualizado.');
  if (input.kind === 'training' && !title) throw new Error('Informe o treinamento.');
  if (input.kind === 'communication' && !audience && !body) throw new Error('Informe a comunicação.');
  let auditResult: AuditResult | null = null;
  if (input.kind === 'audit') {
    if (result !== 'pass' && result !== 'fail' && result !== 'partial') {
      throw new Error('Informe o resultado do acompanhamento.');
    }
    auditResult = result;
  }
  return {
    title,
    body,
    audience: input.kind === 'communication' ? audience : null,
    result: auditResult,
  };
}

export const STANDARDIZATION_KIND_LABEL: Record<StandardizationKind, string> = {
  standard: 'Padrão',
  training: 'Treinamento',
  communication: 'Comunicação',
  audit: 'Acompanhamento',
};

export const AUDIT_RESULT_LABEL: Record<AuditResult, string> = {
  pass: 'Aprovado',
  fail: 'Reprovado',
  partial: 'Parcial',
};

export function formatEventDate(value: string | null | undefined): string {
  if (!value) return '';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return `${match[3]}/${match[2]}/${match[1]}`;
}
